<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import {
  above, below, mastClearance, passableBridgeLevel, gridToPoints, keelClearance, passableLevel,
  seabedLevel, waterDepth, addOffsets, shallowOffsets, deriveExtrema, deriveCurve, gridExtrema, mergeGrids, peakAround, topsAround,
  reshapes, timeshiftMinutes, NO_OFFSETS, type Extremum, type LevelPoint, type Offsets
} from '@quicktide/shared'
import { useAppStore } from '../store'
import { levelsBetween, extremaBetween, levelAt, addDays, shiftPoints, startOfLocalDay, type Point } from '../lib/tide'
import { raise, bestCurve } from '../lib/series'
import TideChart from '../components/TideChart.vue'
import DateStrip from '../components/DateStrip.vue'
import InfoTip from '../components/InfoTip.vue'
import PlaceTabs from '../components/PlaceTabs.vue'
import PlaceTitle from '../components/PlaceTitle.vue'

const store = useAppStore()
const points = ref<Point[]>([])
const extrema = ref<Extremum[]>([])
/** True when the curve on screen was rebuilt from the highs and lows because the source publishes no curve */
const reconstructed = ref(false)
const now = ref(Date.now())
let timer: number | undefined

const dayEnd = computed(() => addDays(store.selectedDay, 1))
const MARGIN = 600_000
const DAY = 86_400_000

/**
 * How much time the chart shows, and where.
 *
 * The day stays the unit everything else is about -- the high and low waters, the windows
 * with enough water, the words under the chart are all about the chosen day. The chart is
 * free to show less of it or more around it: three hours when the question is when exactly
 * to leave, a month when the question is which week to sail in.
 *
 * Inside a day, panning moves within the day; zoomed out past a day, the day sits in the
 * middle. So whatever the chart shows, the chosen day is on it.
 */
const SPANS = [3, 6, 12, 24, 48, 24 * 7, 24 * 30]
const spanHours = computed({
  get: () => store.settings.chartSpanHours,
  set: h => { store.settings.chartSpanHours = h }
})
const panMs = ref(0)

const viewFrom = computed(() => {
  const span = spanHours.value * 3_600_000
  if (span >= DAY) return store.selectedDay + DAY / 2 - span / 2 + panMs.value
  const room = DAY - span
  return store.selectedDay + Math.max(0, Math.min(room, panMs.value))
})
const viewTo = computed(() => viewFrom.value + spanHours.value * 3_600_000)

/**
 * One rung up or down the ladder of spans, holding the moment under the fingers still.
 * Zooming about the middle instead would slide whatever is being looked at off the chart.
 */
function zoomBy(direction: 1 | -1, about: number) {
  const at = SPANS.indexOf(spanHours.value)
  const next = SPANS[Math.max(0, Math.min(SPANS.length - 1, (at < 0 ? 3 : at) + direction))]!
  if (next === spanHours.value) return
  const fraction = (about - viewFrom.value) / (viewTo.value - viewFrom.value)
  spanHours.value = next
  setWindow(about - fraction * next * 3_600_000)
}

function setWindow(from: number) {
  const span = spanHours.value * 3_600_000
  panMs.value = span >= DAY ? from - (store.selectedDay + DAY / 2 - span / 2)
    : Math.max(0, Math.min(DAY - span, from - store.selectedDay))
}

function panBy(by: number) {
  setWindow(viewFrom.value + by)
}

/**
 * The high and low waters, grouped by the day they fall on. One day needs no headings;
 * a week of them needs one per day, and tapping it makes that day the chosen one — which
 * is how the old app let a reader walk forward through the table.
 */
const extremaByDay = computed(() => {
  const days: { start: number; label: string; extrema: typeof extrema.value }[] = []
  for (const e of extrema.value) {
    const start = startOfLocalDay(e.time)
    let day = days[days.length - 1]
    if (!day || day.start !== start) {
      day = {
        start,
        label: new Date(start).toLocaleDateString(store.settings.locale, { weekday: 'long', day: 'numeric', month: 'long' }),
        extrema: []
      }
      days.push(day)
    }
    day.extrema.push(e)
  }
  return days
})

function goToDay(start: number) {
  store.selectedDay = start
}

/**
 * Tapping a high or low water brings it under the reading line, as the old table did: it sets
 * the day if the row belongs to another one, brings the moment on screen where the chart shows
 * less than a day, and pins the reading to it -- so the answer to "what will it be like at that
 * high water" is the number at the top, without hunting for the moment on the curve.
 *
 * The pin goes in on the next tick because changing the day unpins, which is right for every
 * other way of changing it.
 */
function goToExtremum(e: Extremum) {
  const at = shownExtremum(e).time
  const day = startOfLocalDay(at)
  if (day !== store.selectedDay) {
    store.selectedDay = day
    panMs.value = 0
  }
  if (spanHours.value < 24) setWindow(at - (spanHours.value * 3_600_000) / 2)
  void nextTick(() => { markerTime.value = at })
}

/**
 * "When is the water this high?"
 *
 * The chart answers the other question all day -- what the water does at a given moment --
 * and this is its inverse, which is what a skipper asks when a sill or a jetty has a
 * number on it. The line starts at whatever the water is doing now and is dragged from
 * there; the times it cuts the curve come out underneath.
 */
const levelCursor = ref<number | null>(null)
const crossings = computed(() => {
  if (levelCursor.value === null) return []
  return above(best.value, levelCursor.value)
    .filter(w => w.to > viewFrom.value && w.from < viewTo.value)
})
function toggleCursor() {
  levelCursor.value = levelCursor.value === null ? Math.round(levelNow.value ?? 0) : null
}

/** A day of its own again, which is where every other screen starts */
function wholeDay() {
  spanHours.value = 24
  panMs.value = 0
}

// a different day, or a different place, starts from the day again
watch(() => store.selectedDay, () => { panMs.value = 0 })

/**
 * Everything between the station's tide and the place on screen: a derived location's eight
 * offsets, a shallow's own shift, or both when a shallow hangs off a derived location.
 */
const offsets = computed<Offsets>(() => {
  const sub = store.sublocation
  const own = store.shallow ?? store.bridge
  const base: Offsets = sub ? { ...sub } : NO_OFFSETS
  return own ? addOffsets(base, shallowOffsets(own.hwOffsetMinutes, own.lwOffsetMinutes)) : base
})
/** Nothing to derive: the screen is a plain station */
const derived = computed(() => store.shallow !== null || store.bridge !== null || store.sublocation !== null)
/**
 * With height offsets, or a high water that moves differently from the low, the curve has to
 * be rebuilt between the moved extremes. A plain time shift can move the grid as it is,
 * which keeps the real ten-minute detail.
 */
const rebuilds = computed(() => reshapes(offsets.value))
const springLag = computed(() => store.station?.springLagDays)
/** The time shift for a curve that is only moved, not reshaped */
const offset = computed(() => (rebuilds.value ? 0 : timeshiftMinutes(offsets.value)) * 60_000)
/** The widest shift any tide takes, so a query window covers all of them */
const extremeSpan = computed(() => {
  const o = offsets.value
  const all = [o.hwLater2, o.hwLater8, o.lwLater2, o.lwLater8, 0]
  return { min: Math.min(...all) * 60_000, max: Math.max(...all) * 60_000 }
})

/** Derive a grid of levels for a derived place, by moving its extremes and rebuilding */
function rebuild(points: LevelPoint[], from: number, to: number): LevelPoint[] {
  const ex = deriveExtrema(gridExtrema(points), offsets.value, springLag.value)
  return ex.length < 2 ? [] : deriveCurve(ex, from, to)
}

async function load() {
  const id = store.settings.stationId
  if (!id) { points.value = []; extrema.value = []; return }
  const dayFrom = Math.min(store.selectedDay, viewFrom.value) - MARGIN
  const dayTo = Math.max(dayEnd.value, viewTo.value) + MARGIN
  // reach wide enough that the extremes bracketing the day are in hand before they move
  const wide = 12 * 3600_000
  const all = await extremaBetween(id, dayFrom - wide - extremeSpan.value.max, dayTo + wide - extremeSpan.value.min)
  const moved = derived.value ? deriveExtrema(all, offsets.value, springLag.value) : all

  if (rebuilds.value) {
    reconstructed.value = false
    points.value = moved.length < 2 ? [] : deriveCurve(moved, dayFrom, dayTo)
  } else {
    // a little margin so the curve reaches the edges and "now" can be interpolated at midnight
    points.value = shiftPoints(await levelsBetween(id, dayFrom - offset.value, dayTo - offset.value), offset.value)
    // a gauge the source publishes no curve for (BSH: Süderoogsand) gets one rebuilt from
    // its highs and lows, the way a derived place always has -- and the screen says so
    reconstructed.value = !points.value.length && moved.length >= 2
    if (reconstructed.value) points.value = deriveCurve(moved, dayFrom, dayTo)
  }
  const listFrom = Math.min(store.selectedDay, viewFrom.value)
  const listTo = Math.max(dayEnd.value, viewTo.value)
  extrema.value = moved.filter(e => e.time >= listFrom && e.time <= listTo)
}

watch(() => [store.settings.stationId, store.settings.shallowId, store.settings.sublocationId,
  store.selectedDay, store.dataVersion, viewFrom.value, viewTo.value], load, { immediate: true })
onMounted(() => { timer = window.setInterval(() => { now.value = Date.now() }, 30_000) })
onUnmounted(() => window.clearInterval(timer))

/**
 * The measured and forecast levels reach a derived place the same way its prediction does:
 * the old app passes the graph kind straight through the derivation. Where the curve is only
 * moved we move the grid, keeping the real detail of the measurements; where it is reshaped
 * we take the grid's own extremes through the offsets and rebuild between them.
 *
 * The two are merged before deriving, not after. Rebuilding only reaches between extremes, so
 * deriving them separately leaves a hole either side of the moment the measurements stop --
 * which is roughly now, exactly where the reading has to come from.
 */
const actualsGrid = computed(() => mergeGrids(store.stationActuals?.observed, store.stationActuals?.forecast))
/** The span the screen works with: the selected day, whatever the chart shows around it, and today */
const span = computed(() => {
  const today = startOfLocalDay(now.value)
  return {
    from: Math.min(store.selectedDay, viewFrom.value, today) - MARGIN,
    to: Math.max(dayEnd.value, viewTo.value, today + DAY) + MARGIN
  }
})
const derivedActuals = computed<Point[]>(() => {
  const g = actualsGrid.value
  if (!g) return []
  // a day either side, so the extremes at the edges of the span still find their measured
  // peak; and always today, so the reading at the top does not depend on which day is chosen
  const from = span.value.from - DAY
  const to = span.value.to + DAY
  if (!rebuilds.value) return shiftPoints(gridToPoints(g, from - offset.value, to - offset.value), offset.value)
  return rebuild(gridToPoints(g), from, to)
})

/** Where the measurements stop at the place, not at the station */
const lastObservedTime = computed(() => {
  const g = store.stationActuals?.observed
  if (!g) return null
  const pts = gridToPoints(g)
  if (!pts.length) return null
  const last = pts[pts.length - 1]!.time
  return last + (rebuilds.value ? timeshiftMinutes(offsets.value) * 60_000 : offset.value)
})

/** Within what the chart shows, whether that is the day or the week around it */
const inView = (p: Point) => p.time >= Math.min(store.selectedDay, viewFrom.value) - MARGIN && p.time <= Math.max(dayEnd.value, viewTo.value) + MARGIN
/**
 * The skipper's own rise or fall, in cm. While it is set it takes the place of the measured and
 * forecast levels everywhere on this screen: the astronomy stays as it is, and the raised curve
 * is drawn beside it as the account of what the water does. See `raise`.
 */
const surge = computed(() => store.settings.surge)
const surged = computed<Point[]>(() => (surge.value ? raise(points.value, surge.value) : []))

/** Measured levels within the shown span */
const observed = computed<Point[]>(() => {
  const last = lastObservedTime.value
  return surge.value || last === null ? [] : derivedActuals.value.filter(p => inView(p) && p.time <= last)
})
/** Forecast within the shown span, only from where the measurements stop */
const forecast = computed<Point[]>(() => {
  const from = lastObservedTime.value ?? -Infinity
  return !surge.value && store.stationActuals?.forecast ? derivedActuals.value.filter(p => inView(p) && p.time >= from) : []
})

/**
 * The best account of the water: the skipper's own rise or fall, else the measurements and the
 * forecast where they reach, else the astronomy. Everything that asks "when is there enough"
 * -- the level line, the windows over a shallow and under a bridge -- asks it of this, because
 * a window worked out from the moon alone is the one the wind has just closed.
 */
const best = computed<Point[]>(() => bestCurve(points.value, surge.value ? [] : derivedActuals.value, [], surge.value))

/**
 * The high and low waters as the water actually took them.
 *
 * The yearly prediction is astronomy alone: it knows the moon and not the wind. The gauge and
 * the weather forecast know both, and on this coast that is the difference between a metre of
 * water over a shallow and none. So where the measurements or the forecast cover a predicted
 * high or low water, the table shows the time and level they give it, and says which it is;
 * beyond where they reach -- which is a couple of days out -- the astronomy stands alone.
 *
 * Keyed by the astronomical time, which is what the row is built from and unique within it.
 */
/** `tops` only where the water has two: the times of both, in order (see topsAround) */
type Actual = { time: number; level: number; source: 'observed' | 'forecast' | 'surge'; tops?: { time: number; level: number }[] }
const actualExtrema = computed(() => {
  const found = new Map<number, Actual>()
  if (surge.value) {
    for (const e of extrema.value) found.set(e.time, { time: e.time, level: e.level + surge.value, source: 'surge' })
    return found
  }
  const pts = derivedActuals.value
  if (!pts.length) return found
  const last = lastObservedTime.value
  for (const e of extrema.value) {
    const peak = peakAround(pts, e.time, e.type)
    if (!peak) continue
    const tops = topsAround(pts, e.time, e.type)
    found.set(e.time, {
      time: peak.time, level: Math.round(peak.level), source: last !== null && peak.time <= last ? 'observed' : 'forecast',
      ...(tops.length > 1 ? { tops: tops.map(t => ({ time: t.time, level: Math.round(t.level) })) } : {})
    })
  }
  return found
})
const actual = (e: Extremum) => actualExtrema.value.get(e.time) ?? null
/** The level the clearance and the headroom are worked out from: what the water does, if known */
const shownExtremum = (e: Extremum) => actual(e) ?? e
/** The high and low waters as the chart marks them: where the water takes them, where that is known -- both tops of a double one */
const shownExtrema = computed<Extremum[]>(() => extrema.value.flatMap(e => {
  const a = actual(e)
  if (a?.tops) return a.tops.map(t => ({ ...e, time: t.time, level: t.level }))
  return a ? [{ ...e, time: a.time, level: a.level }] : [e]
}))
const anyDouble = computed(() => [...actualExtrema.value.values()].some(a => a.tops))
/** Type, the two times and levels, and the clearance where a shallow or a bridge is chosen */
const extremaColumns = computed(() => 5 + (shallow.value || bridge.value ? 1 : 0))

/**
 * Where the boat sits along the day. Null means it follows the clock; tapping or dragging the
 * chart pins it somewhere else, so the reading answers "what if I were here at that time".
 */
const markerTime = ref<number | null>(null)
const shownTime = computed(() => markerTime.value ?? now.value)
const pinned = computed(() => markerTime.value !== null)
watch(() => [store.selectedDay, store.settings.stationId, store.settings.shallowId, store.settings.bridgeId, store.settings.sublocationId],
  () => { markerTime.value = null })

const astroNow = computed(() => levelAt(points.value, shownTime.value))

/** The best estimate of the level right now: a fresh measurement, else the forecast, else astronomy */
const current = computed<{ level: number; source: 'observed' | 'forecast' | 'surge' | 'astronomical'; at?: number } | null>(() => {
  const a = store.stationActuals
  const t = shownTime.value
  const last = lastObservedTime.value
  if (surge.value) return astroNow.value === null ? null : { level: astroNow.value + surge.value, source: 'surge' }
  // pinned to a moment: measured where the measurements reach, else forecast, else astronomy
  if (pinned.value) {
    const l = levelAt(derivedActuals.value, t)
    if (l !== null) return { level: l, source: last !== null && t <= last ? 'observed' : 'forecast', at: t }
    return astroNow.value === null ? null : { level: astroNow.value, source: 'astronomical' }
  }
  const fresh = 40 * 60_000
  if (last !== null && last >= t - fresh) {
    const pts = derivedActuals.value.filter(p => p.time <= last && p.time >= t - fresh)
    const latest = pts[pts.length - 1]
    if (latest) return { level: latest.level, source: 'observed', at: latest.time }
  }
  if (a?.forecast) {
    const l = levelAt(derivedActuals.value, t)
    if (l !== null) return { level: l, source: 'forecast' }
  }
  return astroNow.value === null ? null : { level: astroNow.value, source: 'astronomical' }
})
const deviation = computed(() => {
  if (!current.value || current.value.source === 'astronomical' || astroNow.value === null) return null
  return (current.value.level - astroNow.value) / 100
})

/**
 * The level in whole centimetres, which is what the data is and what the screen shows. The
 * curve is interpolated, so a headroom that reads exactly 0.50 m can be 0.4998 underneath and
 * compare as too little; rounding once here keeps the number and its colour agreeing.
 */
const levelNow = computed(() => (current.value === null ? null : Math.round(current.value.level)))

// ---- under a bridge ----

const bridge = computed(() => store.bridge)
/** The level the tide has to stay below, and the headroom now */
const bridgeLimit = computed(() => (bridge.value ? passableBridgeLevel(bridge.value.height, store.settings.mastHeight, store.settings.mastClearance) : null))
const nowHeadroom = computed(() => (bridge.value && levelNow.value !== null ? mastClearance(levelNow.value, bridge.value.height, store.settings.mastHeight) : null))
/** How much the tide still has to fall before the boat fits, in cm */
const tooHigh = computed(() => {
  if (bridgeLimit.value === null || levelNow.value === null) return null
  const over = levelNow.value - bridgeLimit.value
  return over > 0 ? over : null
})
/** The stretches of the shown day low enough to pass under */
const bridgeWindows = computed(() => (bridgeLimit.value === null ? [] : below(best.value, bridgeLimit.value).filter(w => w.to > store.selectedDay && w.from < dayEnd.value)))
/** What to draw on the chart: a mast under a bridge, a keel over a shallow, or nothing */
const vessel = computed(() => {
  if (store.bridge) return { mastHeight: store.settings.mastHeight }
  if (store.shallow) return { draught: store.settings.draught }
  return null
})
/** The thing the boat has to clear: the bridge underside, or the seabed */
const measureTo = computed(() => (store.bridge ? store.bridge.height : store.soundedDepth !== null ? seabedLevel(store.soundedDepth) : null))
/** Green when there is room to spare, red when there is not */
const fits = computed(() => {
  if (store.bridge) return nowHeadroom.value !== null && nowHeadroom.value >= store.settings.mastClearance
  if (store.shallow) return nowClearance.value !== null && nowClearance.value >= store.settings.keelClearance
  return false
})

/** "04:13–08:18", or "hele dag" when the window never closes within the day */
function windowLabel(w: { from: number; to: number; openStart: boolean; openEnded: boolean }) {
  if (w.from <= store.selectedDay && w.to >= dayEnd.value) return store.t('allDay')
  const from = `${w.openStart ? '‹' : ''}${fmtTime(Math.max(w.from, store.selectedDay))}`
  const to = `${fmtTime(Math.min(w.to, dayEnd.value))}${w.openEnded ? '›' : ''}`
  return `${from}–${to}`
}

function headroomAt(level: number) {
  return bridge.value ? mastClearance(level, bridge.value.height, store.settings.mastHeight) : null
}

// ---- over a shallow ----

const shallow = computed(() => store.shallow)
const seabed = computed(() => (store.soundedDepth !== null ? seabedLevel(store.soundedDepth) : null))
const passable = computed(() => (store.soundedDepth !== null ? passableLevel(store.soundedDepth, store.settings.draught, store.settings.keelClearance) : null))
/** Water over the shallow now, and what is left under the keel */
const nowDepth = computed(() => (store.soundedDepth !== null && levelNow.value !== null ? waterDepth(levelNow.value, store.soundedDepth) : null))
const nowClearance = computed(() => (store.soundedDepth !== null && levelNow.value !== null ? keelClearance(levelNow.value, store.soundedDepth, store.settings.draught) : null))
/** Below the seabed there is no water at all: the spot has fallen dry */
const dry = computed(() => nowDepth.value !== null && nowDepth.value <= 0)
/** How much the tide still has to rise before the boat fits, in cm */
const shortfall = computed(() => {
  if (passable.value === null || levelNow.value === null) return null
  const missing = passable.value - levelNow.value
  return missing > 0 ? missing : null
})
/** The stretches of the shown day with enough water to cross */
const windows = computed(() => (passable.value === null ? [] : above(best.value, passable.value).filter(w => w.to > store.selectedDay && w.from < dayEnd.value)))

/**
 * The water under the keel at the high water inside a window: the one number that says
 * "can I get over", and how much margin there is if I do. Taken from what the water does.
 */
function hwInWindow(w: { from: number; to: number }) {
  const hw = shownExtrema.value.find(e => e.type === 'HW' && e.time >= w.from && e.time <= w.to)
  return hw ? { time: hw.time, clearance: clearanceAt(hw.level) } : null
}

/**
 * On a watershed the question is "when can I get across", so the windows go on the chart
 * itself, as times across the top, and the table under it explains them.
 */
const chartBands = computed(() => {
  const ws = shallow.value ? windows.value : bridge.value ? bridgeWindows.value : []
  return ws.map(w => ({ state: 'passable', from: w.from, to: w.to, label: windowLabel(w) }))
})

/** "Norderney Riffgat", or "Juist, afgeleid van Norderney Riffgat" when there is a place between */
const chainLabel = computed(() => {
  const sub = store.sublocation
  const station = store.station?.name ?? ''
  return sub ? `${sub.name} (${store.t('derivedFrom')} ${station})` : station
})

/** "+35 min", or "HW +30, LW +20 min" where the two tides shift differently */
const shiftLabel = computed(() => {
  const s = shallow.value
  if (!s) return ''
  const signed = (n: number) => `${n > 0 ? '+' : '−'}${Math.abs(n)}`
  if (s.hwOffsetMinutes === s.lwOffsetMinutes) return `${signed(s.hwOffsetMinutes)} min`
  return `${store.t('high')} ${signed(s.hwOffsetMinutes)}, ${store.t('low')} ${signed(s.lwOffsetMinutes)} min`
})

function clearanceAt(level: number) {
  return store.soundedDepth !== null ? keelClearance(level, store.soundedDepth, store.settings.draught) : null
}
function fmtTime(t: number) {
  return new Date(t).toLocaleTimeString(store.settings.locale, { hour: '2-digit', minute: '2-digit' })
}
function fmtDev(m: number) {
  return `${m >= 0 ? '+' : '−'}${Math.abs(m).toFixed(2)} m`
}
function fmtMetres(cm: number) {
  return `${(cm / 100).toFixed(2)} m`
}
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(store.settings.locale, { year: 'numeric', month: 'short' })
}
</script>

<template>
  <main class="screen tidescreen">
    <div class="header">
      <PlaceTitle />
      <PlaceTabs />
    </div>
    <!-- one column on a phone; on a wide screen the chart on the left and the lists beside it -->
    <div class="tidecols">
    <div class="chartcol">
    <p v-if="!shallow && store.settings.sublocationId && store.sublocation" class="small muted derived">
      {{ store.t('derivedFrom') }} {{ store.station?.name }}<template v-if="store.sublocation.note"> · {{ store.sublocation.note }}</template>
    </p>
    <p v-if="bridge" class="small muted derived">
      {{ store.t('clearanceHeight') }} {{ fmtMetres(bridge.height) }} {{ store.t('aboveDatum') }} {{ store.napDatumName }}<template v-if="bridge.surveyed"> · {{ fmtDate(bridge.surveyed) }}</template>
      · {{ store.t('tideFrom') }} {{ chainLabel }}
    </p>
    <p v-if="bridge?.note" class="notice">{{ bridge.note }}</p>
    <p v-if="shallow" class="small muted derived">
      <template v-if="store.ownDepth">{{ store.t('myDepth') }} {{ fmtMetres(store.ownDepth.depth) }} {{ store.t('belowDatum') }} {{ store.napDatumName }} · {{ store.t('surveyed') }} {{ fmtDate(store.ownDepth.at) }}</template>
      <template v-else>{{ store.t('chartedDepth') }} {{ fmtMetres(shallow.depth) }} {{ store.t('belowDatum') }} {{ store.napDatumName }}<template v-if="shallow.surveyed"> · {{ store.t('surveyed') }} {{ fmtDate(shallow.surveyed) }}</template></template>
      · {{ store.t('tideFrom') }} {{ chainLabel }}<template v-if="shallow.hwOffsetMinutes || shallow.lwOffsetMinutes">, {{ shiftLabel }}</template>
    </p>
    <p v-if="reconstructed" class="small muted derived">{{ store.t('curveReconstructed') }}</p>

    <div v-if="store.place" class="bigline">
      <template v-if="bridge">
        <!-- A negative headroom is not a measurement of anything, so say it in words and put
             the one number that matters -- how far the tide still has to fall -- beside it. -->
        <span v-if="nowHeadroom !== null && nowHeadroom < 0" class="level">{{ store.t('doesNotFit') }}</span>
        <span v-else class="level num">{{ nowHeadroom === null ? '–' : (nowHeadroom / 100).toFixed(2) }}</span>
        <span class="unit"><template v-if="nowHeadroom === null || nowHeadroom >= 0">m {{ store.t('underTheBridge') }}</template> · {{ pinned ? store.t('at') : store.t('now') }} {{ fmtTime(shownTime) }}</span>
        <button v-if="pinned" type="button" class="link nowbtn" @click="markerTime = null">{{ store.t('backToNow') }}</button>
        <span v-if="tooHigh !== null" class="source bad">
          {{ store.tv('mustFall', fmtMetres(tooHigh)) }}
          <span class="dev num"> · {{ store.t('mastHeight') }} {{ fmtMetres(store.settings.mastHeight) }} + {{ fmtMetres(store.settings.mastClearance) }}</span>
        </span>
        <span v-else-if="nowHeadroom !== null" class="source">
          {{ store.t('fitsNow') }}
          <span class="dev num"> · {{ store.t('mastHeight') }} {{ fmtMetres(store.settings.mastHeight) }} + {{ fmtMetres(store.settings.mastClearance) }}</span>
        </span>
      </template>
      <template v-else-if="shallow">
        <span v-if="dry" class="level">{{ store.t('dry') }}</span>
        <span v-else class="level num">{{ nowDepth === null ? '–' : (nowDepth / 100).toFixed(2) }}</span>
        <span class="unit"><template v-if="!dry">m {{ store.t('waterOverShallow') }}</template> · {{ pinned ? store.t('at') : store.t('now') }} {{ fmtTime(shownTime) }}</span>
        <button v-if="pinned" type="button" class="link nowbtn" @click="markerTime = null">{{ store.t('backToNow') }}</button>
        <span v-if="shortfall !== null" class="source bad">
          {{ store.t('tooLittle') }} {{ fmtMetres(shortfall) }}
          <span class="dev num"> · {{ store.t('draught') }} {{ fmtMetres(store.settings.draught) }}</span>
        </span>
        <span v-else-if="nowClearance !== null" class="source">
          {{ store.t('underKeel') }} {{ fmtMetres(nowClearance) }}
          <span class="dev num"> · {{ store.t('draught') }} {{ fmtMetres(store.settings.draught) }}</span>
        </span>
      </template>
      <template v-else>
        <span class="level num">{{ current === null ? '–' : store.displayLevel(current.level).toFixed(2) }}</span>
        <span class="unit">m {{ store.refLevelName }} · {{ pinned ? store.t('at') : store.t('now') }} {{ fmtTime(shownTime) }}</span>
        <button v-if="pinned" type="button" class="link nowbtn" @click="markerTime = null">{{ store.t('backToNow') }}</button>
        <span v-if="current" class="source">
          <template v-if="current.source === 'observed'">{{ store.t('measuredAt') }} {{ fmtTime(current.at!) }}</template>
          <template v-else-if="current.source === 'forecast'">{{ store.t('forecastAt') }}</template>
          <template v-else-if="current.source === 'surge'">{{ store.t('surgeLine') }}</template>
          <template v-else>{{ store.t('astronomical') }}</template>
          <span v-if="deviation !== null" class="dev num"> · {{ fmtDev(deviation) }} {{ store.t('deviation') }}</span>
        </span>
      </template>
    </div>

    <TideChart :points="points" :observed="observed" :forecast="forecast" :surged="surged" :extrema="shownExtrema" :bands="chartBands" :seabed="seabed" :passable="passable" :ceiling="bridge ? bridge.height : null" :limit="bridgeLimit"
               :vessel="vessel" :vessel-level="levelNow" :measure-to="measureTo" :fits="fits"
               :marker-time="markerTime" @update:marker-time="markerTime = $event"
               :level-cursor="levelCursor" @update:level-cursor="levelCursor = $event"
               :day-start="viewFrom" :day-end="viewTo" :now="now"
               @zoom="zoomBy" @pan="panBy" />
    <div class="zoomstrip">
      <button :disabled="spanHours >= SPANS[SPANS.length - 1]!" :aria-label="store.t('zoomOut')" @click="zoomBy(1, (viewFrom + viewTo) / 2)">−</button>
      <button :disabled="spanHours <= SPANS[0]!" :aria-label="store.t('zoomIn')" @click="zoomBy(-1, (viewFrom + viewTo) / 2)">+</button>
      <span class="num span">{{ spanHours < 48 ? `${spanHours} ${store.t('hours')}` : `${Math.round(spanHours / 24)} ${store.t('spanDays')}` }}</span>
      <!-- moving the window only reads as something of its own inside a day; from a day up, the
           day strip below does the moving and these two would say the same thing twice -->
      <template v-if="spanHours < 24">
        <button :aria-label="store.t('earlier')" @click="panBy(-(viewTo - viewFrom) / 2)">‹</button>
        <button :aria-label="store.t('showLater')" @click="panBy((viewTo - viewFrom) / 2)">›</button>
      </template>
      <button v-if="spanHours !== 24 || panMs !== 0" class="link" @click="wholeDay()">{{ store.t('wholeDay') }}</button>
      <button class="link" :class="{ active: levelCursor !== null }" @click="toggleCursor()">{{ store.t('findLevel') }}</button>
    </div>

    <template v-if="levelCursor !== null">
      <h2>{{ store.tv('waterAbove', store.displayLevel(levelCursor).toFixed(2)) }}</h2>
      <table v-if="crossings.length" class="extrema">
        <tbody>
          <tr v-for="(w, i) in crossings" :key="i">
            <td class="type">{{ windowLabel(w) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="notice">{{ store.t('neverThatHigh') }}</p>
    </template>
    <div v-if="observed.length || forecast.length || surged.length || shallow || bridge" class="legend">
      <span v-if="observed.length" class="observed">{{ store.t('observed') }}</span>
      <span v-if="forecast.length" class="forecast">{{ store.t('forecast') }}</span>
      <span v-if="surged.length" class="surged">{{ store.t('surgeLine') }}</span>
      <span class="astro">{{ store.t('astronomical') }}</span>
      <span v-if="shallow" class="passable">{{ store.t('passableLevel') }}</span>
      <span v-if="bridge" class="passable">{{ store.t('passableUnder') }}</span>
    </div>
    <DateStrip />
    </div>

    <div class="listcol">
    <template v-if="bridge">
      <h2>{{ store.t('lowEnough') }}</h2>
      <table v-if="bridgeWindows.length" class="extrema">
        <tbody>
          <tr v-for="(w, i) in bridgeWindows" :key="i">
            <td class="type">{{ windowLabel(w) }}</td>
            <td class="num muted">{{ store.t('mastHeight') }} {{ fmtMetres(store.settings.mastHeight) }} + {{ fmtMetres(store.settings.mastClearance) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="!points.length" class="notice">{{ store.t('noCurveHere') }}</p>
      <p v-else class="notice warn">{{ store.t('neverLowEnough') }}</p>
    </template>

    <template v-if="shallow">
      <h2>{{ store.t('enoughWater') }}</h2>
      <table v-if="windows.length" class="extrema">
        <tbody>
          <tr v-for="(w, i) in windows" :key="i">
            <td class="type">{{ windowLabel(w) }}</td>
            <td v-if="hwInWindow(w)" class="num" :class="{ bad: (hwInWindow(w)!.clearance ?? 0) < 0 }">
              {{ store.t('high') }} {{ fmtTime(hwInWindow(w)!.time) }} · {{ fmtMetres(hwInWindow(w)!.clearance ?? 0) }} {{ store.t('underKeel') }}
            </td>
            <td v-else class="num muted">{{ store.t('draught') }} {{ fmtMetres(store.settings.draught) }} + {{ fmtMetres(store.settings.keelClearance) }}</td>
          </tr>
        </tbody>
      </table>
      <!-- no curve is not the same as never deep enough, and must never read like it -->
      <p v-if="!windows.length && !points.length" class="notice">{{ store.t('noCurveHere') }}</p>
      <p v-else-if="!windows.length" class="notice warn">{{ store.t('neverEnoughWater') }}</p>
    </template>

    <h2>{{ store.t('highLowWater') }} <InfoTip v-if="extrema.length && actualExtrema.size" :title="store.t('highLowWater')" :texts="[store.t(surge ? 'extremaSurgeNote' : 'extremaActualsNote'), anyDouble ? store.t('doubleTideNote') : '']" /></h2>
    <table v-if="extrema.length" class="extrema paired">
      <!-- what the water does on the left, as the helpdesk keeps hearing it should be; the
           astronomy on the right, for comparison, where the two differ in kind -->
      <thead>
        <tr class="groups">
          <th></th>
          <th colspan="2">{{ surge ? store.t('surgeLine') : actualExtrema.size ? store.t('measuredOrForecast') : store.t('astronomical') }}</th>
          <th colspan="2" class="gap">{{ actualExtrema.size ? store.t('astronomical') : '' }}</th>
          <th v-if="shallow">{{ store.t('keelClearance') }}</th>
          <th v-else-if="bridge">{{ store.t('headroom') }}</th>
        </tr>
        <tr class="units">
          <th></th>
          <th>{{ store.t('timeHeading') }}</th>
          <th>m {{ store.refLevelName }}</th>
          <th class="gap">{{ store.t('timeHeading') }}</th>
          <th>m {{ store.refLevelName }}</th>
          <th v-if="shallow || bridge">m</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="day in extremaByDay" :key="day.start">
          <!-- a date only where the list runs past one day, and only where the day turns -->
          <tr v-if="extremaByDay.length > 1" class="dayrow">
            <td :colspan="extremaColumns">
              <button class="link" @click="goToDay(day.start)">{{ day.label }}</button>
            </td>
          </tr>
          <tr v-for="e in day.extrema" :key="e.time" class="tappable" :class="{ past: e.time < now, here: markerTime === shownExtremum(e).time }"
              role="button" tabindex="0" @click="goToExtremum(e)" @keydown.enter.prevent="goToExtremum(e)" @keydown.space.prevent="goToExtremum(e)">
            <td class="type" :class="e.type.toLowerCase()">{{ e.type === 'HW' ? store.t('high') : store.t('low') }}</td>
            <td class="num" :class="actual(e)?.source">
              <!-- a double high water: both tops, one under the other so the column stays narrow -->
              <template v-if="actual(e)?.tops"><template v-for="(t, k) in actual(e)!.tops" :key="k"><br v-if="k">{{ fmtTime(t.time) }}</template></template>
              <template v-else>{{ fmtTime(shownExtremum(e).time) }}</template>
            </td>
            <td class="num" :class="actual(e)?.source">
              {{ store.displayLevel(shownExtremum(e).level).toFixed(2) }}
              <!-- the word matters more than the colour: measured, forecast and astronomy are different claims -->
              <small v-if="actualExtrema.size" class="src">{{ store.t(actual(e)?.source ?? 'astro') }}</small>
            </td>
            <td class="num gap astro">{{ actual(e) ? fmtTime(e.time) : '·' }}</td>
            <td class="num astro">{{ actual(e) ? store.displayLevel(e.level).toFixed(2) : '·' }}</td>
            <td v-if="shallow" class="num" :class="{ bad: (clearanceAt(shownExtremum(e).level) ?? 0) < 0 }">{{ (clearanceAt(shownExtremum(e).level)! / 100).toFixed(2) }}</td>
            <td v-else-if="bridge" class="num" :class="{ bad: (headroomAt(shownExtremum(e).level) ?? 0) < 0 }">{{ (headroomAt(shownExtremum(e).level)! / 100).toFixed(2) }}</td>
          </tr>
        </template>
      </tbody>
    </table>
    <p v-if="!extrema.length" class="notice">{{ store.t('noData') }}</p>
    </div>
    </div>
  </main>
</template>
